// Scherm 15 — Verkennen, ronde 4 (03-09): de lagenkaart.
//
// Ontwerp: docs/decisions/2026-09-03-verkennen-ronde-4-platformbouw.md en de
// gidsen Dieper graven, Van onderwerp naar bron, Door de lagen. In het kort:
//
//   · AFBAKENING (K1): thema, onderwerp en periode bepalen wat er bestaat.
//     Buiten de afbakening wordt niets getekend, is niets klikbaar en telt
//     niets mee. Filters dimmen. De kop zegt het altijd.
//   · LAGEN (A3): thema's → onderwerpen → punten → één punt met zijn buren en
//     bronnen. Klik gaat dieper, Esc en het kruimelpad gaan terug.
//   · LIJNEN (K2/G3): twee betekenissen, dikte = bronnen, en elk verband is te
//     lezen: herkomst, redenering, de alinea's in de bron.
//   · TIJD (C1/C2): een strook onder de kaart met venster en vergelijking.
//   · SNELLE VRAGEN (E3): elf tellingen, elk een lijst met een leesregel.
//   · DEZELFDE BOUWSTENEN als de rest van het dashboard: FilterBar/FilterMenu,
//     SearchField, FilterChips, SlidePanel, SourceDetail, ChatPopup (via onVraag).
//
// De kaart komt in één verzoek binnen (/api/graaf/kaart) en alles daarna
// rekent in de browser; alleen een verband, een alinea, de bronnen van een
// punt en zoeken op betekenis vragen een eigen rondje.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { EmptyList, LoadError } from "../components/Empty";
import { FilterChips, type Chip } from "../components/FilterChips";
import { FilterMenu, type FilterSection } from "../components/FilterMenu";
import { Lagenkaart, isBevestigd, themaKleur, type GrootteModus, type KleurModus, type Stuur, type Telling, type Weergave } from "../components/Lagenkaart";
import { LeesPaneel } from "../components/LeesPaneel";
import { PuntPaneel } from "../components/PuntPaneel";
import { TrendVanBevinding } from "../components/TrendlaagOud";
import { trendlaagAan } from "../nieuw/trendlaag/schakelaar";
import { SourceDetail } from "../components/SourceDetail";
import { Tijdstrook } from "../components/Tijdstrook";
import { Card, FilterBar, PageHeader, SearchField, SlidePanel } from "../components/ui";
import { VerbandPaneel } from "../components/VerbandPaneel";
import { VerkennenEenvoudig } from "./VerkennenEenvoudig";
import type { GraafBronnenResponse, GraafKaartResponse, GraafPunt, GraafZoekResponse, SourceDetailResponse } from "../contracts";
import { api } from "../lib/api";
import { useMeta } from "../lib/meta";
import { useStartSector } from "../lib/sectorkeuze";
import { verkennenSectoren } from "../lib/verkennenTab";
import { sectorWoord, toonPropositie } from "../nieuw/propositie";
import {
  BANDEN, SNELLE_VRAGEN, aantalFilters, afbakeningLeden, beantwoord, berekenStaat, beweging, bouwOp, burenVan,
  leegFilters, maandKort, maandNr, venster, vergelijkVenster,
  type Afbakening, type BandAs, type Filters, type Periode, type SnelleVraag, type VraagUitkomst,
} from "../lib/verkennen";
import { color, radius, size, weight } from "../tokens";

const SCHOLEN: [string, string[]][] = [
  ["Summa ICT", ["ict"]], ["Summa Artiest", ["media"]], ["Summa Automotive", ["automotive"]],
  ["Summa Beauty & Lifestyle", ["persoonlijk"]], ["Summa Bouw", ["bouw"]],
  ["Summa Brood & Banket", ["procesindustrie", "horeca"]], ["Summa Economie & Ondernemen", ["retail", "zakelijk"]],
  ["Summa Elektro- & Installatietechniek", ["hightech", "energie", "bouw"]], ["Summa Engineering", ["maakindustrie", "hightech"]],
  ["Summa Facilitair", ["zakelijk"]], ["Summa Fashion", ["maakindustrie", "retail"]], ["Summa Horeca", ["horeca"]],
  ["Summa Laboratorium", ["procesindustrie"]], ["Summa Luchtvaartdienstverlening", ["logistiek"]], ["Summa Plus (entree)", []],
  ["Summa Procestechnologie", ["procesindustrie"]], ["Summa Sport", ["persoonlijk"]], ["Summa Techniek", ["maakindustrie", "hightech"]],
  ["Summa Transport & Logistiek", ["logistiek"]], ["Summa Zorg", ["zorg"]],
];
const SECTOREN = ["ict", "zorg", "maakindustrie", "hightech", "zakelijk", "publiek"];

/** De sectorlijst van Verkennen (ADR 2026-10-23-ict-los-platform): eerst de
 *  zichtbare (uit /api/meta), dan de vaste rest als "nog niet gelezen". Met
 *  alleen ICT zichtbaar precies de oude lijst. Pilot gereed 26-10 (1C): ICT
 *  staat er alleen in als ICT zichtbaar is — een verborgen ICT als keuze gaf
 *  een 404. */
export function verkenSectoren(zichtbaar: readonly string[] | undefined): { lijst: string[]; uit: Record<string, string> } {
  const zicht = zichtbaar?.length ? zichtbaar : ["ict"];
  const lijst = [...new Set([...zicht, ...SECTOREN.filter((s) => s !== "ict" || zicht.includes("ict"))])];
  return { lijst, uit: Object.fromEntries(lijst.filter((s) => !zicht.includes(s)).map((s) => [s, "nog niet gelezen"])) };
}
const WEERGAVEN: [Weergave, string][] = [["lagen", "Lagen"], ["alles", "De hele kaart"], ["matrix", "2×2 impact × moeite"], ["matrix2", "2×2 impact × urgentie"]];
const KLEUREN: [KleurModus, string][] = [["soort", "Kleur op soort"], ["beweging", "Kleur op beweging"], ["impact", "Kleur op impact"], ["urgentie", "Kleur op urgentie"], ["uitgever", "Kleur op wie het draagt"]];
const GROOTTES: [GrootteModus, string][] = [["bronnen", "Grootte op bronnen"], ["impact", "Grootte op impact"], ["gelijk", "Grootte gelijk"]];

type Paneel =
  | { soort: "punt"; id: string }
  | { soort: "verband"; van: string; naar: string }
  | { soort: "alinea"; id: string; citaat?: string | null }
  | { soort: "bron"; id: string }
  | { soort: "trend"; id: string };

/** Verkennen: standaard "eenvoudig" (editie 11); de lagenkaart van ronde 4 blijft
 *  bereikbaar onder "meer" tot de owner hem wegdoet (release 06-10). */
export function VerkennenPagina({ onVraag, onPlan }: { onVraag?: (vraag: string) => void; onPlan?: () => void }) {
  const [oud, setOud] = useState(false);
  // De eigen propositie als die zichtbaar is (ADR 2026-10-23-ict-los-platform).
  const start = useStartSector();
  // Pilot gereed 26-10 (1C): openen op een sector waar de graaf voor bestaat;
  // de eigen propositie als die er een heeft, anders de eerste.
  const verkenbaar = verkennenSectoren(useMeta());
  const begin = verkenbaar.includes(start) ? start : (verkenbaar[0] ?? start);
  const [sector, setSector] = useState(begin);
  useEffect(() => {
    setSector((s) => (verkenbaar.length && !verkenbaar.includes(s) ? begin : s));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [begin, verkenbaar.join(",")]);
  if (oud) return <LagenkaartPagina onVraag={onVraag} beginSector={sector} onEenvoudig={() => setOud(false)} />;
  return <VerkennenEenvoudig sector={sector} onSector={setSector} onVraag={onVraag} onPlan={onPlan} onLagenkaart={() => setOud(true)} />;
}

/** De lagenkaart (ronde 4, 03-09) — de uitgebreide weergave. */
export function LagenkaartPagina({ onVraag, beginSector = "ict", onEenvoudig }: { onVraag?: (vraag: string) => void; beginSector?: string; onEenvoudig?: () => void }) {
  const [data, setData] = useState<GraafKaartResponse | null>(null);
  const [fout, setFout] = useState<string | null>(null);
  const [sector, setSector] = useState(beginSector);
  const meta = useMeta();
  const zicht = meta?.zichtbareSectoren?.length ? meta.zichtbareSectoren : ["ict"];
  const verken = verkenSectoren(zicht);
  const [school, setSchool] = useState<string[]>([]);
  const [vermoed, setVermoed] = useState(false);
  // methode v3: alles wat niet "bevestigd" is dimmen
  const [alleenBevestigd, setAlleenBevestigd] = useState(false);
  const [afbakening, setAfbakening] = useState<Afbakening>({ thema: null, onderwerp: null });
  const [periode, setPeriode] = useState<Periode | null>(null);
  const [vergelijk, setVergelijk] = useState(false);
  const [verschuiving, setVerschuiving] = useState(0);
  const [filters, setFilters] = useState<Filters>(leegFilters);
  const [weergave, setWeergave] = useState<Weergave>("lagen");
  const [kleurModus, setKleurModus] = useState<KleurModus>("soort");
  const [grootteModus, setGrootteModus] = useState<GrootteModus>("bronnen");
  const [gekozen, setGekozen] = useState<string | null>(null);
  const [pad, setPad] = useState<string[]>([]);
  const [focus, setFocus] = useState(false);
  const [markering, setMarkering] = useState<{ vraag: SnelleVraag; uit: VraagUitkomst } | null>(null);
  const [gebied, setGebied] = useState<string[] | null>(null);
  const [gebiedModus, setGebiedModus] = useState(false);
  const [paneel, setPaneel] = useState<Paneel | null>(null);
  const [paneel2, setPaneel2] = useState<Paneel | null>(null);
  const [bronnen, setBronnen] = useState<GraafBronnenResponse | null>(null);
  const [bronDetail, setBronDetail] = useState<SourceDetailResponse | null>(null);
  const [telling, setTelling] = useState<Telling | null>(null);
  const [lijstOpen, setLijstOpen] = useState(false);
  const [lijstSort, setLijstSort] = useState<"bronnen" | "impact" | "moeite" | "urgentie" | "laatste">("bronnen");
  const [zoek, setZoek] = useState("");
  const [zoekUit, setZoekUit] = useState<GraafZoekResponse | null>(null);
  const [zoekBezig, setZoekBezig] = useState(false);
  const stuur = useRef<Stuur | null>(null);

  // ── data ──────────────────────────────────────────────────────────────
  useEffect(() => {
    let levend = true;
    setData(null); setFout(null);
    api.graafKaart({ sector, soorten: vermoed ? "bron,ouder_kind,breder_smaller,verwant,onderwerp" : "bron,ouder_kind,breder_smaller,onderwerp" })
      .then((d) => levend && setData(d))
      .catch((e) => levend && setFout(String(e)));
    return () => { levend = false; };
  }, [sector, vermoed]);

  const ob = useMemo(() => (data ? bouwOp(data) : null), [data]);
  // Methode v3: hoeveel punten dragen de laag "bevestigd"? 0 = de API levert
  // geen bewijs (leeslaag v1) of niets is bevestigd: dan is er geen filter.
  const aantalBevestigd = useMemo(() => (data ? data.punten.filter((p) => isBevestigd(p)).length : 0), [data]);
  // Trendlaag aan: geen keurmerk "bevestigd" (dekking eindstand 25-10); het filter verdwijnt.
  const laagAan = trendlaagAan();
  const filterBevestigd = !laagAan && alleenBevestigd && aantalBevestigd > 0;

  useEffect(() => {
    if (!gekozen) { setBronnen(null); return; }
    let levend = true;
    setBronnen(null);
    api.graafBronnen(gekozen).then((b) => levend && setBronnen(b)).catch(() => levend && setBronnen({ punt: null as never, bronnen: [] }));
    return () => { levend = false; };
  }, [gekozen]);

  useEffect(() => {
    const p = paneel2?.soort === "bron" ? paneel2 : paneel?.soort === "bron" ? paneel : null;
    if (!p) { setBronDetail(null); return; }
    let levend = true;
    setBronDetail(null);
    api.source(p.id).then((d) => levend && setBronDetail(d)).catch(() => undefined);
    return () => { levend = false; };
  }, [paneel, paneel2]);

  // zoeken op betekenis (S2): pas na 400 ms stilte, één rondje per vraag
  useEffect(() => {
    const v = zoek.trim();
    if (v.length < 3) { setZoekUit(null); return; }
    setZoekBezig(true);
    const t = setTimeout(() => {
      api.graafZoek(v, { sector, n: 8 }).then((u) => { setZoekUit(u); setZoekBezig(false); }).catch(() => { setZoekUit(null); setZoekBezig(false); });
    }, 400);
    return () => clearTimeout(t);
  }, [zoek, sector]);

  // ── afgeleide toestand ────────────────────────────────────────────────
  const staatBasis = useMemo(() => (ob ? berekenStaat(ob, afbakening, periode, filters) : new Uint8Array(0)), [ob, afbakening, periode, filters]);
  // focus (G5): alleen het gekozen punt en zijn buren
  const staat = useMemo(() => {
    if (!ob || !focus || !gekozen) return staatBasis;
    const i = ob.index.get(gekozen);
    if (i === undefined) return staatBasis;
    const s = new Uint8Array(staatBasis.length);
    s[i] = staatBasis[i] || 2;
    for (const [j] of ob.buren.get(i) ?? []) if (staatBasis[j]) s[j] = staatBasis[j]!;
    for (const [ow, lijst] of ob.leden) { const oi = ob.index.get(ow); if (oi !== undefined && lijst.some((l) => s[l])) s[oi] = 2; }
    return s;
  }, [ob, staatBasis, focus, gekozen]);

  const nu = ob?.nu ?? 0;
  const v = useMemo(() => venster(periode, nu), [periode, nu]);
  const vVergelijk = useMemo(() => (periode ? vergelijkVenster(periode, verschuiving) : null), [periode, verschuiving]);
  const bewegingVan = useCallback((i: number) => (ob ? beweging(ob.punten[i]!, v) : "onbekend"), [ob, v]);

  const actief = useMemo(() => {
    if (!ob) return [] as number[];
    const uit: number[] = [];
    for (let i = 0; i < ob.punten.length; i++) if (staat[i] === 2 && ob.punten[i]!.soort !== "onderwerp") uit.push(i);
    return uit;
  }, [ob, staat]);
  // punten zonder onderwerp: bestaan wel, maar hangen onder geen enkele bol (alleen op "de hele kaart")
  const zonderOnderwerp = useMemo(() => (ob ? actief.filter((i) => !(ob.onderwerpenVan.get(i)?.length)).length : 0), [ob, actief]);
  const inScope = useMemo(() => {
    if (!ob) return 0;
    let n = 0;
    for (let i = 0; i < ob.punten.length; i++) if (staat[i] && ob.punten[i]!.soort !== "onderwerp") n++;
    return n;
  }, [ob, staat]);

  // bewijs per maand binnen de afbakening (voor de tijdstrook)
  const perMaand = useMemo(() => {
    if (!ob) return [] as number[];
    const tel = new Array<number>(Math.max(1, ob.nu - ob.min + 1)).fill(0);
    const set = afbakeningLeden(ob, afbakening);
    for (let i = 0; i < ob.punten.length; i++) {
      const p = ob.punten[i]!;
      if (p.soort === "onderwerp" || (set && !set.has(i))) continue;
      for (const m of p.bewijsMaanden ?? []) { const n = maandNr(m); if (n !== null && n >= ob.min && n <= ob.nu) tel[n - ob.min]!++; }
    }
    return tel;
  }, [ob, afbakening]);
  const vergelijkTelling = useMemo(() => {
    if (!ob || !vergelijk || !periode || !vVergelijk) return null;
    let nieuw = 0; let bew = 0; let stil = 0;
    const set = afbakeningLeden(ob, afbakening);
    for (let i = 0; i < ob.punten.length; i++) {
      const p = ob.punten[i]!;
      if (p.soort === "onderwerp" || (set && !set.has(i))) continue;
      const mm = (p.bewijsMaanden ?? []).map(maandNr).filter((n): n is number => n !== null);
      const erin = mm.some((n) => n >= periode.van && n <= periode.tot);
      const ervoor = mm.some((n) => n >= vVergelijk.van && n <= vVergelijk.tot);
      if (erin && !ervoor) nieuw++; else if (erin && ervoor) bew++; else if (!erin && ervoor) stil++;
    }
    return { nieuw, beweging: bew, stil };
  }, [ob, vergelijk, periode, vVergelijk, afbakening]);

  const horizons = useMemo(() => tel(ob?.punten ?? [], (p) => p.horizon), [ob]);
  const types = useMemo(() => tel(ob?.punten ?? [], (p) => p.type), [ob]);
  const bandTelling = useMemo(() => {
    const uit: Record<BandAs, Record<string, number>> = { impact: {}, urgentie: {}, moeite: {} };
    for (const p of ob?.punten ?? []) {
      if (p.soort === "onderwerp") continue;
      for (const as of ["impact", "urgentie", "moeite"] as BandAs[]) {
        if (as === "moeite" && p.soort !== "vaardigheid") continue;
        const w = as === "impact" ? p.impact : as === "urgentie" ? p.urgentie : p.moeite;
        if (w == null) continue;
        const g = BANDEN[as].grenzen; let b: number = g.length;
        for (let k = 0; k < g.length; k++) if (w < g[k]!) { b = k; break; }
        const naam = BANDEN[as].namen[b]!;
        uit[as][naam] = (uit[as][naam] ?? 0) + 1;
      }
    }
    return uit;
  }, [ob]);

  // ── acties ────────────────────────────────────────────────────────────
  const zetAfbakening = useCallback((a: Afbakening) => {
    setAfbakening(a); setGekozen(null); setPad([]); setFocus(false); setMarkering(null); setGebied(null); setPaneel(null); setPaneel2(null);
  }, []);
  const onThema = useCallback((naam: string) => zetAfbakening({ thema: naam, onderwerp: null }), [zetAfbakening]);
  const onOnderwerp = useCallback((id: string) => {
    const thema = ob?.themaVan.get(id) ?? null;
    zetAfbakening({ thema, onderwerp: id });
  }, [ob, zetAfbakening]);
  const onKies = useCallback((id: string | null) => {
    setGekozen((vorige) => { if (id && vorige && vorige !== id) setPad((p) => [...p, vorige]); if (!id) { setPad([]); setFocus(false); } return id; });
    setGebied(null);
    setPaneel(id ? { soort: "punt", id } : null);
    setPaneel2(null);
  }, []);
  const terug = useCallback(() => {
    if (pad.length) { const vorige = pad[pad.length - 1]!; setPad((p) => p.slice(0, -1)); setGekozen(vorige); setPaneel({ soort: "punt", id: vorige }); return; }
    if (gekozen) { onKies(null); return; }
    if (afbakening.onderwerp) { zetAfbakening({ thema: afbakening.thema, onderwerp: null }); return; }
    if (afbakening.thema) zetAfbakening({ thema: null, onderwerp: null });
  }, [pad, gekozen, afbakening, onKies, zetAfbakening]);
  useEffect(() => {
    // Esc: eerst het bovenste paneel dicht, dan het volgende, dan een laag terug.
    const onToets = (ev: KeyboardEvent) => {
      if (ev.key !== "Escape") return;
      if (paneel2) setPaneel2(null);
      else if (paneel) setPaneel(null);
      else terug();
    };
    document.addEventListener("keydown", onToets);
    return () => document.removeEventListener("keydown", onToets);
  }, [terug, paneel, paneel2]);
  const onVerband = useCallback((van: string, naar: string) => setPaneel({ soort: "verband", van, naar }), []);
  const onAlinea = useCallback((id: string, citaat?: string | null) => setPaneel2({ soort: "alinea", id, citaat }), []);
  const onTelling = useCallback((t: Telling) => setTelling(t), []);
  const onGebied = useCallback((ids: string[]) => { setGebied(ids); setGebiedModus(false); setMarkering(null); setLijstOpen(true); }, []);
  const kiesEnVlieg = useCallback((id: string) => { onKies(id); setTimeout(() => stuur.current?.vliegNaar(id, 6), 30); }, [onKies]);

  const stelVraag = useCallback((vraag: SnelleVraag) => {
    if (!ob || !data) return;
    const uit = beantwoord(vraag, ob, data.lijnen, staat, afbakening, periode);
    setMarkering({ vraag, uit }); setGebied(null); setLijstOpen(true);
  }, [ob, data, staat, afbakening, periode]);

  // ── de lijst: wat er nu geselecteerd is ───────────────────────────────
  const lijstIds = useMemo(() => {
    if (!ob) return [] as number[];
    let ids: number[];
    if (markering && markering.uit.ids.length) ids = markering.uit.ids;
    else if (gebied) ids = gebied.map((id) => ob.index.get(id)).filter((i): i is number => i !== undefined);
    else ids = actief;
    if (markering && markering.uit.ids.length) return ids;
    const w = (i: number) => {
      const p = ob.punten[i]!;
      switch (lijstSort) {
        case "impact": return p.impact ?? -1;
        case "moeite": return p.moeite ?? -1;
        case "urgentie": return p.urgentie ?? -1;
        case "laatste": return maandNr(p.laatsteBewijs) ?? -1;
        default: return p.bronnen ?? 0;
      }
    };
    return [...ids].sort((a, b) => w(b) - w(a));
  }, [ob, markering, gebied, actief, lijstSort]);
  const markeringSet = useMemo(() => {
    if (!ob) return null;
    if (markering) return new Set(markering.uit.ids.map((i) => ob.punten[i]!.id));
    if (gebied) return new Set(gebied);
    return null;
  }, [ob, markering, gebied]);

  // ── de kop van de afbakening (K1) ─────────────────────────────────────
  const naamVan = (id: string | null) => (id && ob ? ob.punten[ob.index.get(id) ?? -1]?.naam ?? null : null);
  const scopeNaam = afbakening.onderwerp ? naamVan(afbakening.onderwerp) : afbakening.thema;
  const periodeNaam = periode ? `${maandKort(periode.van)} – ${maandKort(periode.tot)}` : "hele periode";
  const chips: Chip[] = [
    ...(afbakening.thema ? [{ axis: "thema", value: afbakening.thema, label: `thema ${afbakening.thema}` }] : []),
    ...(afbakening.onderwerp ? [{ axis: "onderwerp", value: afbakening.onderwerp, label: `onderwerp ${scopeNaam ?? ""}` }] : []),
    ...(periode ? [{ axis: "periode", value: "p", label: periodeNaam }] : []),
    ...(school.length ? [{ axis: "school", value: school[0]!, label: school[0]! }] : []),
    ...(!filters.toon.trend ? [{ axis: "toon", value: "trend", label: "alleen vaardigheden" }] : []),
    ...(!filters.toon.vaardigheid ? [{ axis: "toon", value: "vaardigheid", label: "alleen trends" }] : []),
    ...(["impact", "urgentie", "moeite"] as BandAs[]).flatMap((as) => filters.banden[as].map((b) => ({ axis: as, value: String(b), label: `${as} ${BANDEN[as].namen[b]!.replace(/ \(.*\)/, "")}` }))),
    ...filters.horizons.map((h) => ({ axis: "horizon", value: h, label: h })),
    ...filters.types.map((t) => ({ axis: "type", value: t, label: t })),
    ...(filters.alleenQuickWin ? [{ axis: "qw", value: "1", label: "quick wins" }] : []),
    ...(filters.alleenDrager ? [{ axis: "drager", value: "1", label: "dragers" }] : []),
    ...(focus ? [{ axis: "focus", value: "1", label: "focus op één punt" }] : []),
    ...(filterBevestigd ? [{ axis: "bevestigd", value: "1", label: "alleen bevestigd" }] : []),
  ];
  const wisChip = (c: Chip) => {
    if (c.axis === "thema") zetAfbakening({ thema: null, onderwerp: null });
    else if (c.axis === "onderwerp") zetAfbakening({ thema: afbakening.thema, onderwerp: null });
    else if (c.axis === "periode") setPeriode(null);
    else if (c.axis === "school") setSchool([]);
    else if (c.axis === "toon") setFilters((f) => ({ ...f, toon: { trend: true, vaardigheid: true } }));
    else if (c.axis === "impact" || c.axis === "urgentie" || c.axis === "moeite") setFilters((f) => ({ ...f, banden: { ...f.banden, [c.axis]: f.banden[c.axis as BandAs].filter((b) => String(b) !== c.value) } }));
    else if (c.axis === "horizon") setFilters((f) => ({ ...f, horizons: f.horizons.filter((h) => h !== c.value) }));
    else if (c.axis === "type") setFilters((f) => ({ ...f, types: f.types.filter((t) => t !== c.value) }));
    else if (c.axis === "qw") setFilters((f) => ({ ...f, alleenQuickWin: false }));
    else if (c.axis === "drager") setFilters((f) => ({ ...f, alleenDrager: false }));
    else if (c.axis === "focus") setFocus(false);
    else if (c.axis === "bevestigd") setAlleenBevestigd(false);
  };

  // ── het onderwerpmenu (K4): thema's als assen, onderwerpen als waarden ─
  const onderwerpSecties: FilterSection[] = useMemo(() => {
    if (!ob) return [];
    return ob.themas.map((t) => {
      const namen = t.onderwerpen.map((id) => ob.punten[ob.index.get(id) ?? -1]?.naam ?? id);
      const counts: Record<string, number> = {};
      t.onderwerpen.forEach((id, k) => { counts[namen[k]!] = (ob.leden.get(id) ?? []).filter((i) => staatBasis[i] === 2).length; });
      return {
        key: t.naam, label: t.naam,
        uitleg: `${t.onderwerpen.length} onderwerpen · klik het kopje om het hele thema af te bakenen`,
        options: namen, counts,
        values: afbakening.onderwerp && t.onderwerpen.includes(afbakening.onderwerp) ? [naamVan(afbakening.onderwerp) ?? ""] : [],
        allesTegelijk: true,
        onChange: (next: string[]) => {
          if (next.length === namen.length && namen.length > 1) { zetAfbakening({ thema: t.naam, onderwerp: null }); return; }
          if (next.length === 0 && afbakening.thema === t.naam && !afbakening.onderwerp) { zetAfbakening({ thema: t.naam, onderwerp: null }); return; }
          const gekozenNaam = next.find((n) => n !== naamVan(afbakening.onderwerp)) ?? next[0];
          if (!gekozenNaam) { zetAfbakening({ thema: null, onderwerp: null }); return; }
          const id = t.onderwerpen[namen.indexOf(gekozenNaam)];
          if (id) zetAfbakening({ thema: t.naam, onderwerp: id });
        },
      };
    });

  }, [ob, staatBasis, afbakening]);

  // ── het filtermenu (G6): banden en assen naast elkaar ─────────────────
  const filterSecties: FilterSection[] = [
    { key: "toon", label: "Tonen", uitleg: "Niets aangevinkt = allebei.", options: ["Trends", "Vaardigheden"],
      values: filters.toon.trend && filters.toon.vaardigheid ? [] : [...(filters.toon.trend ? ["Trends"] : []), ...(filters.toon.vaardigheid ? ["Vaardigheden"] : [])],
      onChange: (n) => setFilters((f) => ({ ...f, toon: n.length === 0 || n.length === 2 ? { trend: true, vaardigheid: true } : { trend: n.includes("Trends"), vaardigheid: n.includes("Vaardigheden") } })) },
    ...(["impact", "urgentie", "moeite"] as BandAs[]).map((as) => ({
      key: as, label: as === "moeite" ? "Moeite (vaardigheden)" : as.charAt(0).toUpperCase() + as.slice(1),
      uitleg: as === "moeite" ? "Alleen vaardigheden dragen moeite; trends doen altijd mee." : "Vier benoemde banden in plaats van een schuif.",
      options: [...BANDEN[as].namen], counts: bandTelling[as],
      values: filters.banden[as].map((b) => BANDEN[as].namen[b]!),
      onChange: (n: string[]) => setFilters((f) => ({ ...f, banden: { ...f.banden, [as]: n.map((x) => BANDEN[as].namen.indexOf(x as never)).filter((i) => i >= 0) } })),
    })),
    { key: "horizon", label: "Horizon", options: horizons.map(([h]) => h), counts: Object.fromEntries(horizons), values: filters.horizons, onChange: (n) => setFilters((f) => ({ ...f, horizons: n })) },
    { key: "type", label: "Soort vaardigheid", options: types.map(([t]) => t), counts: Object.fromEntries(types), values: filters.types, onChange: (n) => setFilters((f) => ({ ...f, types: n })) },
    { key: "extra", label: "Alleen", options: ["Quick wins", "Dragers, geen marktcijfers"], values: [...(filters.alleenQuickWin ? ["Quick wins"] : []), ...(filters.alleenDrager ? ["Dragers, geen marktcijfers"] : [])],
      onChange: (n) => setFilters((f) => ({ ...f, alleenQuickWin: n.includes("Quick wins"), alleenDrager: n.includes("Dragers, geen marktcijfers") })) },
  ];

  if (fout) return <LoadError reason={fout} />;

  const gekozenIdx = ob && gekozen ? ob.index.get(gekozen) : undefined;
  const gekozenPunt = gekozenIdx !== undefined ? ob!.punten[gekozenIdx] : undefined;
  const laagNaam = gekozen ? "rond één punt" : afbakening.onderwerp ? "punten" : afbakening.thema ? "onderwerpen" : "thema's";

  return (
    <>
      {onEenvoudig && (
        <button type="button" onClick={onEenvoudig} style={{ marginBottom: 8, cursor: "pointer", background: "none", border: 0, color: color.accentBlue, fontSize: size.meta, textDecoration: "underline", padding: 0 }}>
          ← terug naar de eenvoudige kaart
        </button>
      )}
      <PageHeader
        title="Verkennen"
        count={
          !ob ? "laden…" : (
            <>
              <b style={{ color: color.ink, fontWeight: weight.medium }}>{actief.length.toLocaleString("nl-NL")} punten</b>
              {scopeNaam ? <> in <b style={{ color: color.ink, fontWeight: weight.medium }}>{scopeNaam}</b></> : " op de hele kaart"}
              {" · "}{sectorWoord(meta, sector).toUpperCase()}{" · "}{periodeNaam}
              {aantalFilters(filters) > 0 && inScope !== actief.length ? ` · ${actief.length} van ${inScope} voldoen aan de filters` : ""}
              {weergave === "lagen" ? ` · laag: ${laagNaam}` : ""}
              {(scopeNaam || periode || focus || gekozen) && (
                <button type="button" onClick={() => { zetAfbakening({ thema: null, onderwerp: null }); setPeriode(null); }} style={{ marginLeft: 10, cursor: "pointer", background: "none", border: 0, color: color.accentBlue, fontSize: size.meta, textDecoration: "underline", padding: 0 }}>
                  alles loslaten
                </button>
              )}
            </>
          )
        }
      />

      <FilterBar chips={chips.length ? <FilterChips chips={chips} onRemove={wisChip} onClearAll={() => { zetAfbakening({ thema: null, onderwerp: null }); setPeriode(null); setFilters(leegFilters()); setSchool([]); setAlleenBevestigd(false); }} /> : undefined}>
        <div style={{ position: "relative" }}>
          <SearchField value={zoek} onChange={setZoek} placeholder="Zoek op betekenis: onderwerp, trend, vaardigheid…" width={280} />
          {(zoekUit || zoekBezig) && zoek.trim().length >= 3 && (
            <ul style={{ position: "absolute", zIndex: 30, left: 0, top: "100%", marginTop: 4, width: 420, listStyle: "none", padding: 0, maxHeight: 360, overflow: "auto", background: color.surface, border: `1px solid ${color.border}`, borderRadius: radius.input, boxShadow: "0 8px 24px -12px rgba(0,0,0,0.3)" }}>
              {ob && ob.themas.filter((t) => t.naam.toLowerCase().includes(zoek.trim().toLowerCase())).map((t) => (
                <li key={`t:${t.naam}`}><button type="button" onClick={() => { setZoek(""); onThema(t.naam); }} style={zoekRij}>{t.naam}<span style={zoekSub}> · thema</span></button></li>
              ))}
              {ob && ob.punten.filter((p) => p.soort === "onderwerp" && p.naam.toLowerCase().includes(zoek.trim().toLowerCase())).slice(0, 4).map((p) => (
                <li key={p.id}><button type="button" onClick={() => { setZoek(""); onOnderwerp(p.id); }} style={zoekRij}>{p.naam}<span style={zoekSub}> · onderwerp · {(ob.leden.get(p.id) ?? []).length} punten</span></button></li>
              ))}
              {zoekBezig && !zoekUit && <li style={{ padding: "7px 11px", fontSize: size.label, color: color.faint }}>zoeken op betekenis…</li>}
              {zoekUit?.treffers.map((t) => (
                <li key={t.id}><button type="button" onClick={() => { setZoek(""); if (staat[ob?.index.get(t.id) ?? -1] === 0) zetAfbakening({ thema: null, onderwerp: null }); kiesEnVlieg(t.id); }} style={zoekRij}>
                  {t.naam}<span style={zoekSub}> · {t.soort} · {t.bronnen} bron{t.bronnen === 1 ? "" : "nen"} · {Math.round(t.gelijkenis * 100)}%</span>
                </button></li>
              ))}
              {zoekUit && zoekUit.treffers.length === 0 && <li style={{ padding: "7px 11px", fontSize: size.label, color: color.faint }}>Niets gevonden.</li>}
            </ul>
          )}
        </div>

        <FilterMenu label={`${meta && toonPropositie(meta) ? "Propositie" : "Sector"}: ${sectorWoord(meta, sector).toUpperCase()}`} options={verken.lijst} values={[]} onChange={(n) => { const v2 = n.find((x) => x !== sector) ?? sector; setSector(v2); }}
          disabled={verken.uit} />
        <FilterMenu label="School" options={SCHOLEN.filter(([, s]) => !s.includes("ict") || zicht.includes("ict")).map(([naam]) => naam)} values={school} onChange={(n) => setSchool(n.slice(-1))}
          disabled={Object.fromEntries(SCHOLEN.filter(([, s]) => !s.some((x) => zicht.includes(x))).map(([naam]) => [naam, "nog geen punten voor deze school"]))}
          note={zicht.length === 1 && zicht[0] === "ict"
            ? "Elke school hangt aan sectoren; zolang alleen ICT gelezen is, heeft alleen Summa ICT punten."
            : "Elke school hangt aan sectoren; een school zonder gelezen sector heeft nog geen punten."} />
        <FilterMenu label={scopeNaam ? `Onderwerp: ${scopeNaam}` : "Onderwerp"} sections={onderwerpSecties} note="Klik een thema-kopje voor het hele thema, of één onderwerp. Nog eens klikken laat los." />
        <FilterMenu label={aantalFilters(filters) ? `Filters (${aantalFilters(filters)})` : "Filters"} sections={filterSecties} />
        <FilterMenu label="Beeld"
          options={[...WEERGAVEN.map(([, l]) => l), ...KLEUREN.map(([, l]) => l), ...GROOTTES.map(([, l]) => l), "Ook AI-vermoedens (verwant)"]}
          values={[...(weergave !== "lagen" ? [WEERGAVEN.find(([w]) => w === weergave)![1]] : []), ...(kleurModus !== "soort" ? [KLEUREN.find(([k]) => k === kleurModus)![1]] : []), ...(grootteModus !== "bronnen" ? [GROOTTES.find(([g]) => g === grootteModus)![1]] : []), ...(vermoed ? ["Ook AI-vermoedens (verwant)"] : [])]}
          onChange={(next) => {
            // één keuze per rij: de nieuw aangevinkte wint, uitvinken = terug naar de standaard
            const w = WEERGAVEN.find(([w2, l]) => next.includes(l) && w2 !== weergave);
            setWeergave(w ? w[0] : WEERGAVEN.some(([, l]) => next.includes(l)) ? weergave : "lagen");
            const k = KLEUREN.find(([k2, l]) => next.includes(l) && k2 !== kleurModus);
            setKleurModus(k ? k[0] : KLEUREN.some(([, l]) => next.includes(l)) ? kleurModus : "soort");
            const g = GROOTTES.find(([g2, l]) => next.includes(l) && g2 !== grootteModus);
            setGrootteModus(g ? g[0] : GROOTTES.some(([, l]) => next.includes(l)) ? grootteModus : "bronnen");
            setVermoed(next.includes("Ook AI-vermoedens (verwant)"));
          }} />
        <FilterMenu label={markering ? `Snelle vragen: ${markering.vraag.nr}` : "Snelle vragen"}
          sections={(["Vinden", "Bewegen", "Onderbouwen"] as const).map((groep) => ({
            key: groep, label: groep,
            uitleg: groep === "Vinden" ? "Wat hoort waar: elke vraag levert een lijst én een kleur op de kaart." : groep === "Bewegen" ? "Alle vier rekenen op de periode in de tijdstrook." : "Hoe hard is het: elke rij klikt door naar de alinea en de bron.",
            options: SNELLE_VRAGEN.filter((q) => q.groep === groep).map((q) => q.naam),
            values: markering && markering.vraag.groep === groep ? [markering.vraag.naam] : [],
            onChange: (n) => { const naam = n.find((x) => x !== markering?.vraag.naam) ?? n[0]; const q = SNELLE_VRAGEN.find((x) => x.naam === naam); if (q) stelVraag(q); else setMarkering(null); },
          }))} />

        {!laagAan && aantalBevestigd > 0 && (
          <button type="button" aria-pressed={alleenBevestigd} onClick={() => setAlleenBevestigd((a) => !a)} style={knopStijl(alleenBevestigd)}
            title={`${aantalBevestigd.toLocaleString("nl-NL")} trends zijn bevestigd: genoeg opstellers zeggen hetzelfde. Aan = de rest van de kaart dimt.`}>
            Alleen bevestigd
          </button>
        )}
        <button type="button" onClick={() => setLijstOpen((o) => !o)} style={{ ...knopStijl(lijstOpen), marginLeft: "auto" }}>
          Lijst {lijstIds.length.toLocaleString("nl-NL")} {lijstOpen ? "›" : "‹"}
        </button>
        <button type="button" onClick={() => { setGebiedModus((g) => !g); setGebied(null); }} style={knopStijl(gebiedModus)}>Gebied kiezen</button>
        {onVraag && (
          <button type="button" onClick={() => onVraag(chatContext(scopeNaam, periodeNaam, actief.length, gekozenPunt?.naam))} style={{ ...knopStijl(false), background: color.ink, color: color.surface, borderColor: color.ink }}>
            Vraag de chat
          </button>
        )}
      </FilterBar>

      {!data || !ob ? (
        <Card style={{ padding: 40, textAlign: "center", color: color.faint, fontSize: size.meta }}>de kaart laden…</Card>
      ) : ob.punten.length === 0 ? (
        <EmptyList reason={`Voor de sector ${sector} zijn nog geen trends of vaardigheden gelezen. De kaart vult zich zodra die ronde gedraaid is.`} />
      ) : (
        // Kaart en lijst naast elkaar; past dat niet (smal scherm), dan komt de
        // lijst eronder. Een vaste kolom van 380 px maakte de pagina op 390 px
        // 42 px te breed (gemeten 01-10).
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-start" }}>
          <div style={{ position: "relative", flex: "1 1 520px", minWidth: 0 }}>
            {(afbakening.thema || afbakening.onderwerp || gekozen) && weergave === "lagen" && (
              <div style={{ position: "absolute", zIndex: 3, top: 10, left: 10, display: "flex", alignItems: "center", gap: 6, background: color.surface, border: `1px solid ${color.line}`, borderRadius: 9, padding: "5px 8px 5px 12px", fontSize: size.meta, maxWidth: "calc(100% - 120px)", boxShadow: "0 6px 20px -12px rgba(0,0,0,0.35)" }}>
                <button type="button" onClick={() => zetAfbakening({ thema: null, onderwerp: null })} style={kruimel}>Alles</button>
                {afbakening.thema && <><span style={{ color: color.faint }}>›</span><button type="button" onClick={() => zetAfbakening({ thema: afbakening.thema, onderwerp: null })} style={{ ...kruimel, color: themaKleur(ob.themas, afbakening.thema) }}>{afbakening.thema}</button></>}
                {afbakening.onderwerp && <><span style={{ color: color.faint }}>›</span><button type="button" onClick={() => onKies(null)} style={kruimel}>{scopeNaam}</button></>}
                {gekozen && <><span style={{ color: color.faint }}>›</span><span style={{ color: color.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{gekozenPunt?.naam}</span></>}
                <button type="button" onClick={terug} style={{ ...knopStijl(false), padding: "2px 8px", marginLeft: 4 }}>Terug</button>
              </div>
            )}
            {markering && (
              <div style={{ position: "absolute", zIndex: 3, right: 56, top: 10, background: color.surface, border: `1px solid ${color.line}`, borderRadius: 9, padding: "6px 10px", fontSize: size.label, color: color.body, maxWidth: 360, boxShadow: "0 6px 20px -12px rgba(0,0,0,0.35)" }}>
                <b style={{ fontWeight: weight.medium }}>{markering.vraag.naam}</b> · {markering.uit.regel}
                <button type="button" onClick={() => setMarkering(null)} style={{ ...knopStijl(false), padding: "1px 7px", marginLeft: 8 }}>✕</button>
              </div>
            )}
            <Lagenkaart
              data={data} ob={ob} staat={staat} afbakening={afbakening} periode={periode} weergave={weergave}
              kleurModus={kleurModus} grootteModus={grootteModus} gekozen={gekozen} markering={markeringSet}
              bronnen={bronnen?.bronnen ?? null} vermoed={vermoed} alleenBevestigd={filterBevestigd}
              onThema={onThema} onOnderwerp={onOnderwerp} onKies={onKies} onVerband={onVerband} onAlinea={onAlinea}
              onTelling={onTelling} onGebied={onGebied} gebiedModus={gebiedModus} stuur={stuur}
            />
            <div style={{ position: "absolute", left: 10, bottom: 10, fontSize: size.label, color: color.faint, background: color.surface, border: `1px solid ${color.line}`, borderRadius: 8, padding: "4px 9px", pointerEvents: "none" }}>
              {telling ? `${telling.punten.toLocaleString("nl-NL")} punten · ${telling.lijnen} verbanden · ${telling.zoom.toFixed(1)}× · ${telling.niveau}` : ""}
              {weergave === "lagen" && !afbakening.thema && zonderOnderwerp > 0 ? ` · ${zonderOnderwerp.toLocaleString("nl-NL")} punten zonder onderwerp staan alleen op de hele kaart` : ""}
              {data.afwezig.zonderPlek ? ` · ${data.afwezig.zonderPlek} punten zonder plek op de kaart` : ""}
              {data.afwezig.zonderDatum ? ` · ${data.afwezig.zonderDatum} zonder bewijsdatum` : ""}
              {data.versie === "gemengd" ? " · kaart half bijgewerkt" : ""}
            </div>
            <Tijdstrook
              min={ob.min} nu={ob.nu} perMaand={perMaand} periode={periode} vergelijk={vergelijk} vergelijkVenster={vVergelijk}
              telling={vergelijkTelling}
              onPeriode={(p) => { setPeriode(p); setVerschuiving(0); }}
              onVergelijk={(aan) => { setVergelijk(aan); if (aan && !periode) setPeriode({ van: ob.nu - 11, tot: ob.nu }); if (aan) setKleurModus("beweging"); }}
              onVerschuif={setVerschuiving}
            />
            <Legenda kleurModus={kleurModus} themas={ob.themas} weergave={weergave} bevestigd={laagAan ? 0 : aantalBevestigd} />
          </div>

          {lijstOpen && (
            <Card style={{ padding: "12px 14px", maxHeight: 760, overflow: "auto", position: "sticky", top: 12, flex: "0 1 380px", minWidth: 0, maxWidth: "100%" }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                <h3 style={{ margin: 0, fontSize: size.lead, fontWeight: weight.medium, flex: 1 }}>
                  {markering ? markering.vraag.naam : gebied ? "Gekozen gebied" : scopeNaam ?? "Alles op de kaart"}
                </h3>
                <button type="button" onClick={() => setLijstOpen(false)} aria-label="Lijst sluiten" style={{ ...knopStijl(false), padding: "2px 8px" }}>✕</button>
              </div>
              <div style={{ fontSize: size.label, color: color.faint, margin: "3px 0 8px" }}>
                {markering ? markering.uit.regel : `${lijstIds.length.toLocaleString("nl-NL")} van ${inScope.toLocaleString("nl-NL")} punten · ${periodeNaam}`}
                {markering && <div style={{ marginTop: 3 }}>Zo lees je het: {markering.vraag.lees}</div>}
              </div>
              {!markering && (
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 8 }}>
                  {(["bronnen", "impact", "moeite", "urgentie", "laatste"] as const).map((s) => (
                    <button key={s} type="button" onClick={() => setLijstSort(s)} style={{ ...knopStijl(lijstSort === s), padding: "2px 8px", fontSize: 11 }}>{s}</button>
                  ))}
                  <button type="button" onClick={() => kopieer(lijstIds.map((i) => ob.punten[i]!).map((p) => [p.naam, p.soort, p.impact ?? "", p.moeite ?? "", p.urgentie ?? "", p.bronnen, p.laatsteBewijs ?? ""].join("\t")).join("\n"))} style={{ ...knopStijl(false), padding: "2px 8px", fontSize: 11, marginLeft: "auto" }}>kopieer als tabel</button>
                </div>
              )}
              {markering?.uit.paren && (
                <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                  {markering.uit.paren.map((pr) => (
                    <li key={`${pr.a}-${pr.b}`} style={{ display: "flex", gap: 6, alignItems: "center", padding: "5px 0", borderTop: `1px solid ${color.lineFaint}`, fontSize: size.meta }}>
                      <button type="button" onClick={() => onOnderwerp(pr.a)} style={{ all: "unset", cursor: "pointer", color: color.ink }}>{naamVan(pr.a)}</button>
                      <span style={{ color: color.faint }}>×</span>
                      <button type="button" onClick={() => onOnderwerp(pr.b)} style={{ all: "unset", cursor: "pointer", color: color.ink }}>{naamVan(pr.b)}</button>
                      <span style={{ marginLeft: "auto", fontSize: size.label, color: color.faint, whiteSpace: "nowrap" }}>{pr.was !== undefined ? `${pr.was} → ${pr.nu} gedeeld` : "0 verbanden"}</span>
                      {onVraag && pr.was === undefined && <button type="button" onClick={() => onVraag(`Wat verbindt “${naamVan(pr.a)}” en “${naamVan(pr.b)}”? Welke vaardigheid of trend zou de brug zijn, en welke bron onderbouwt dat?`)} style={{ ...knopStijl(false), padding: "1px 7px", fontSize: 11 }}>brugvraag</button>}
                    </li>
                  ))}
                </ul>
              )}
              {markering?.uit.onderwerpen && (
                <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                  {markering.uit.onderwerpen.map((o) => (
                    <li key={o.id} style={{ display: "flex", gap: 6, alignItems: "center", padding: "5px 0", borderTop: `1px solid ${color.lineFaint}`, fontSize: size.meta }}>
                      <button type="button" onClick={() => onOnderwerp(o.id)} style={{ all: "unset", cursor: "pointer", color: color.ink, flex: 1 }}>{naamVan(o.id)}</button>
                      <span style={{ fontSize: size.label, color: o.nu > o.was ? color.accentRed : o.nu < o.was ? color.faint : color.soft, whiteSpace: "nowrap" }}>{o.was} → {o.nu}{o.nu > o.was ? " ▲" : o.nu < o.was ? " ▼" : ""}</span>
                    </li>
                  ))}
                </ul>
              )}
              {!markering?.uit.paren && !markering?.uit.onderwerpen && (
                // tableLayout fixed: de naam krimpt mee (ellips) in plaats van de
                // lijst zijwaarts te laten schuiven (gemeten 01-10: 34 px).
                <table style={{ width: "100%", tableLayout: "fixed", borderCollapse: "collapse", fontSize: size.meta, fontVariantNumeric: "tabular-nums" }}>
                  <thead><tr>{["naam", "imp", "moe", "urg", "bron", "beweging"].map((h) => <th key={h} style={{ width: h === "naam" ? "auto" : h === "beweging" ? 88 : 36, textAlign: h === "naam" || h === "beweging" ? "left" : "right", fontSize: 10, fontWeight: weight.medium, letterSpacing: "0.06em", textTransform: "uppercase", color: color.faint, padding: "4px 4px", borderBottom: `1px solid ${color.lineSoft}`, position: "sticky", top: 0, background: color.surface }}>{h}</th>)}</tr></thead>
                  <tbody>
                    {lijstIds.slice(0, 200).map((i) => {
                      const p = ob.punten[i]!;
                      const b = bewegingVan(i);
                      return (
                        <tr key={p.id} onClick={() => kiesEnVlieg(p.id)} style={{ cursor: "pointer", background: p.id === gekozen ? color.surfaceQuote : "transparent" }}>
                          <td style={{ padding: "4px", borderBottom: `1px solid ${color.lineFaint}`, maxWidth: 170, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: color.body }} title={p.naam}>
                            <i style={{ display: "inline-block", width: 7, height: 7, borderRadius: "50%", background: p.soort === "trend" ? "#0d9488" : "#f59e0b", marginRight: 5 }} />{p.naam}
                            {markering?.uit.toelichting.get(i) && <div style={{ fontSize: 10, color: color.faint }}>{markering.uit.toelichting.get(i)}</div>}
                          </td>
                          <td style={cel}>{p.impact ?? "–"}</td><td style={cel}>{p.moeite ?? "–"}</td><td style={cel}>{p.urgentie ?? "–"}</td><td style={cel}>{p.bronnen}</td>
                          <td style={{ padding: "4px", borderBottom: `1px solid ${color.lineFaint}` }}><span style={{ fontSize: 10, padding: "1px 6px", borderRadius: 99, border: `1px solid ${color.border}`, color: b === "opkomend" ? color.accentRed : b === "stil" ? color.faint : color.body, whiteSpace: "nowrap" }}>{b === "onbekend" ? "geen datum" : b}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
              {lijstIds.length > 200 && <p style={{ fontSize: size.label, color: color.faint, margin: "8px 0 0" }}>De eerste 200 van {lijstIds.length}; verfijn de afbakening of de filters voor de rest.</p>}
            </Card>
          )}
        </div>
      )}

      {/* Laag 1: punt of verband. Laag 2: de alinea of de bron. */}
      <SlidePanel open={paneel !== null} onClose={() => { setPaneel(null); if (paneel?.soort === "punt") { /* het punt blijft gekozen op de kaart */ } }} terugLabel="Terug naar de kaart">
        {paneel?.soort === "punt" && ob && gekozenPunt && gekozenIdx !== undefined && data && (
          <PuntPaneel
            punt={gekozenPunt}
            buren={burenVan(ob, data.lijnen, gekozenIdx, staat, bewegingVan).filter((b) => vermoed || b.soort !== "verwant")}
            puntVan={(i) => ob.punten[i]!}
            beweging={bewegingVan(gekozenIdx)}
            onderwerpen={(ob.onderwerpenVan.get(gekozenIdx) ?? []).map((ow) => `${ob.themaVan.get(ow)} › ${naamVan(ow)}`)}
            bronnen={bronnen}
            vlaggen={[...((gekozenPunt.bronnen ?? 0) >= 8 ? ["draagt veel"] : [])]}
            onClose={() => setPaneel(null)}
            onKies={(id) => { if (staat[ob.index.get(id) ?? -1] === 0) zetAfbakening({ thema: null, onderwerp: null }); kiesEnVlieg(id); }}
            onVerband={onVerband}
            onOpenAlinea={onAlinea}
            onFocus={() => setFocus((f) => !f)}
            focusAan={focus}
            onVraag={onVraag}
            onOpenTrend={laagAan ? () => setPaneel2({ soort: "trend", id: gekozenPunt.id }) : undefined}
          />
        )}
        {paneel?.soort === "verband" && (
          <VerbandPaneel van={paneel.van} naar={paneel.naar} onClose={() => setPaneel(gekozen ? { soort: "punt", id: gekozen } : null)}
            onOpenPunt={(id) => kiesEnVlieg(id)} onOpenAlinea={onAlinea} onVraag={onVraag} />
        )}
      </SlidePanel>
      <SlidePanel level={2} open={paneel2 !== null} onClose={() => setPaneel2(null)} terugLabel={paneel?.soort === "verband" ? "Terug naar het verband" : "Terug naar het punt"}>
        {paneel2?.soort === "alinea" && (
          <LeesPaneel alineaId={paneel2.id} citaat={paneel2.citaat} onClose={() => setPaneel2(null)}
            onOpenAlinea={(id, citaat) => setPaneel2({ soort: "alinea", id, citaat })}
            onOpenBron={(documentId) => setPaneel2({ soort: "bron", id: documentId })} />
        )}
        {paneel2?.soort === "trend" && <TrendVanBevinding id={paneel2.id} onClose={() => setPaneel2(null)} />}
        {paneel2?.soort === "bron" && (
          bronDetail
            ? <SourceDetail source={bronDetail.source} otherFromPublisher={bronDetail.otherFromPublisher} onClose={() => setPaneel2(null)} />
            : <div style={{ padding: 24, color: color.faint, fontSize: size.meta }}>bron laden…</div>
        )}
      </SlidePanel>
    </>
  );
}

// ── hulpjes ───────────────────────────────────────────────────────────────

const zoekRij = { display: "block", width: "100%", textAlign: "left", cursor: "pointer", background: "none", border: 0, padding: "7px 11px", fontSize: size.meta, color: color.body, fontFamily: "inherit" } as const;
const zoekSub = { color: color.faint, fontSize: size.label } as const;
const kruimel = { all: "unset", cursor: "pointer", color: color.body, fontSize: size.meta, whiteSpace: "nowrap" } as const;
const cel = { padding: "4px", borderBottom: `1px solid ${color.lineFaint}`, textAlign: "right", color: color.body } as const;

function knopStijl(aan: boolean) {
  return {
    cursor: "pointer", fontSize: size.meta, padding: "7px 12px", borderRadius: radius.input, fontFamily: "inherit",
    border: `1px solid ${aan ? color.ink : color.border}`, background: aan ? color.ink : color.surface, color: aan ? color.surface : color.body,
  } as const;
}

function tel(punten: readonly GraafPunt[], veld: (p: GraafPunt) => string | null | undefined): [string, number][] {
  const m = new Map<string, number>();
  for (const p of punten) { const v = veld(p); if (v) m.set(v, (m.get(v) ?? 0) + 1); }
  return [...m.entries()].sort((a, b) => b[1] - a[1]);
}

function kopieer(tekst: string) {
  if (navigator.clipboard?.writeText) void navigator.clipboard.writeText(tekst);
}

/** Wat de chat meekrijgt: de afbakening in gewone woorden, geen lijst van 300 namen. */
function chatContext(scope: string | null, periode: string, n: number, punt?: string): string {
  if (punt) return `Wat zeggen de bronnen over “${punt}”, en welke vaardigheden vraagt dit van ons onderwijs?`;
  return `Wat is de rode draad in ${scope ? `het onderwerp ${scope}` : "de hele kaart"} (${n} punten, ${periode}), en welke vaardigheden volgen daaruit voor ons onderwijs?`;
}

function Legenda({ kleurModus, themas, weergave, bevestigd = 0 }: { kleurModus: KleurModus; themas: readonly { naam: string }[]; weergave: Weergave; bevestigd?: number }) {
  const bol = (kl: string, tekst: string, rand?: string) => (
    <span key={tekst} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <i style={{ width: 9, height: 9, borderRadius: "50%", background: rand ? "transparent" : kl, border: rand ? `1.5px ${rand} ${kl}` : "none", display: "inline-block" }} />{tekst}
    </span>
  );
  const items = kleurModus === "beweging"
    ? [bol("#ef4444", "opkomend in de periode"), bol("#3b82f6", "in beweging"), bol("#cbd5e1", "stil"), bol("#94a3b8", "geen datum")]
    : kleurModus === "uitgever"
      ? [bol("#3b82f6", "beleid"), bol("#7c3aed", "onderzoek"), bol("#0d9488", "arbeidsmarktdata"), bol("#f59e0b", "bedrijfsleven"), bol("#ec4899", "vakmedia"), bol("#10b981", "onderwijs"), bol("#94a3b8", "gemengd")]
      : kleurModus === "soort"
        ? [bol("#0d9488", "trend"), bol("#f59e0b", "vaardigheid"), bol("#7c3aed", "onderwerp")]
        : [bol("#ef4444", "hoog"), bol("#94a3b8", "laag")];
  return (
    <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: "6px 16px", fontSize: size.label, color: color.faint, alignItems: "center" }}>
      {weergave === "lagen" && themas.slice(0, 8).map((t) => bol(themaKleur(themas, t.naam), t.naam))}
      {weergave !== "lagen" || kleurModus !== "soort" ? items : null}
      {bol("#ef4444", "ring = groei of opkomend", "solid")}
      {bevestigd > 0 && bol(color.accentGreen, `groene ring = bevestigd (${bevestigd.toLocaleString("nl-NL")} trends)`, "solid")}
      <span><i style={{ display: "inline-block", width: 18, borderTop: "2px solid #0d9488", verticalAlign: "middle", marginRight: 6 }} />vraagt om (dikte = bronnen)</span>
      <span><i style={{ display: "inline-block", width: 18, borderTop: "2px dashed #f59e0b", verticalAlign: "middle", marginRight: 6 }} />hoort bij</span>
      <span><i style={{ display: "inline-block", width: 18, borderTop: "3px solid #7c3aed", opacity: 0.5, verticalAlign: "middle", marginRight: 6 }} />gedeelde vaardigheden tussen bollen</span>
      <span>Lichte punten vallen buiten de filters; wat buiten de afbakening valt, staat er niet.</span>
    </div>
  );
}
